<template>
  <li class="game-condition-item choice">
    <dropdown-component
      class="game-award-dropdown"
      :value="awardSelection"
      :invalid-message="validationErrors.gameAward"
      :disabled="disableFields"
      @change="handleGameAwardChange"
    >
      <template v-if="awardSelectedObject" #internal-caption>
        <span class="icon">
          <template v-if="awardSelectedObject.type === 'GAME_STORY'">
            <GameStoryIcon />
          </template>
          <template v-else-if="awardSelectedObject.type === 'ACHIEVEMENT'">
            <AchievementIcon />
          </template>
          <template v-else>
            <GameAvatarIcon />
          </template>
        </span>

        <ellipsis-text :text="awardSelectedObject.title" />
      </template>

      <cv-dropdown-item
        v-for="gameAward in processGameAwards"
        :key="`${gameAward.id}${gameAward.title}`"
        :value="gameAward.id.toString()"
      >
        <span class="icon">
          <template v-if="gameAward.type === 'GAME_STORY'">
            <GameStoryIcon />
          </template>
          <template v-else-if="gameAward.type === 'ACHIEVEMENT'">
            <AchievementIcon />
          </template>
          <template v-else>
            <GameAvatarIcon />
          </template>
        </span>

        <ellipsis-text :text="gameAward.title" />
      </cv-dropdown-item>
    </dropdown-component>
    <dropdown-component
      class="game-choice-dropdown"
      :value="gameChoiceSelected"
      :invalid-message="validationErrors.gameChoice"
      :disabled="choiceDropdownDisabled"
      @change="handleGameChoiceChange"
    >
      <template v-if="gameChoiceSelectedObject" #internal-caption>
        <span class="icon">
          <CorrectBranchIcon v-if="isSelectGameChoiceType" />
          <div v-else-if="isTextInputGameChoiceType">
            <CorrectBranchIcon v-if="gameChoiceSelectedObject.acceptableResponse" />
            <WrongBranchIcon v-else />
          </div>
        </span>

        <ellipsis-text :text="gameChoiceSelectedObject?.choices" />
      </template>

      <cv-dropdown-item
        v-for="gameChoice in processGameChoices"
        :key="`${gameChoice.id}${gameChoice.choices}`"
        :value="gameChoice.id.toString()"
      >
        <span class="icon">
          <CorrectBranchIcon v-if="isSelectGameChoiceType" />
          <div v-else-if="isTextInputGameChoiceType">
            <CorrectBranchIcon v-if="gameChoice.acceptableResponse" />
            <WrongBranchIcon v-else />
          </div>
        </span>

        <ellipsis-text :text="gameChoice.choices" />
      </cv-dropdown-item>
    </dropdown-component>
    <cv-button kind="ghost" class="icon-button btn-delete-condition">
      <svg-icon name="Trash24" kind="action" @click="deleteCondition" />
    </cv-button>
  </li>
</template>

<script setup lang="ts">
import GameStoryIcon from "@images/icons/indicative/32px/GameStory32.svg?component";
import AchievementIcon from "@images/icons/indicative/32px/Achievement32.svg?component";
import GameAvatarIcon from "@images/icons/indicative/32px/GameAvatar32.svg?component";
import { isDefined, isEmpty, isNull } from "@utils/var.check";
import { GAME_AWARD_TYPE, GAME_CHOICE_TYPE } from "@modules/editor/service/gamification.constant";
import CorrectBranchIcon from "@images/icons/indicative/32px/CorrectBranch32.svg?component";
import WrongBranchIcon from "@images/icons/indicative/32px/WrongBranch32.svg?component";
import { ref, computed, watch, onMounted } from "vue";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["change", "delete"]);
const { t } = useI18n();

// Props
interface Props {
  condition: object;
  targetGameAward?: object;
  gameAwards?: unknown[]; // Replace `any` with your game award type
  disableFields?: boolean;
  validationErrors?: object;
  isFromGameStory?: boolean;
}

const {
  condition,
  targetGameAward,
  gameAwards = [],
  disableFields = false,
  validationErrors = {},
  isFromGameStory = false,
} = defineProps<Props>();

// Data
const gameAwardSelected = ref(null);
const gameChoiceSelected = ref(condition.gameChoice ? condition.gameChoice.toString() : null);

// Computed
const awardSelection = computed(() => {
  if (processGameAwards.value.some((award) => award.id.toString() === gameAwardSelected.value)) {
    return gameAwardSelected.value?.toString() ?? null;
  }
  return null;
});

const processGameAwards = computed(() => {
  const processedAwards = gameAwards.filter(
    (award) =>
      (!targetGameAward || award.id !== targetGameAward.id) && !isEmpty(award.gameChoiceList),
  );
  if (isFromGameStory) {
    return processedAwards.filter((award) => award.type === GAME_AWARD_TYPE.GAME_STORY);
  }
  return processedAwards;
});

const processGameChoices = computed(() => {
  if (isNull(gameAwardSelected.value)) {
    return [];
  }

  const selectedGameAward = gameAwards.find(
    (award) => award.id.toString() === gameAwardSelected.value,
  );

  if (!isDefined(selectedGameAward)) return [];

  const isGameChoices = selectedGameAward.choiceType === GAME_CHOICE_TYPE.SELECT;
  if (isGameChoices) return selectedGameAward.gameChoiceList;

  const gameChoiceList = selectedGameAward.gameChoiceList;
  if (gameChoiceList?.length > 0) {
    gameChoiceList.forEach((gameBranch) => {
      if (!gameBranch.acceptableResponse) {
        gameBranch.choices = t("gamification_all_other_responses_label");
      }
    });
  }
  return gameChoiceList;
});

const choiceDropdownDisabled = computed(() => {
  return isNull(gameAwardSelected.value) || disableFields;
});

const selectedGameAward = computed(() => {
  return processGameAwards.value.find(
    (gameAward) => gameAward.id === parseInt(gameAwardSelected.value),
  );
});

const isSelectGameChoiceType = computed(() => {
  return selectedGameAward.value.choiceType === GAME_CHOICE_TYPE.SELECT;
});

const isTextInputGameChoiceType = computed(() => {
  return selectedGameAward.value.choiceType === GAME_CHOICE_TYPE.TEXT_INPUT;
});

const awardSelectedObject = computed(() => {
  return processGameAwards.value.find(
    (item) => item.id.toString() === awardSelection.value?.toString(),
  );
});

const gameChoiceSelectedObject = computed(() => {
  return processGameChoices.value.find(
    (item) => item.id.toString() === gameChoiceSelected.value?.toString(),
  );
});

// Methods
const handleGameAwardChange = function (gameAwardId) {
  gameAwardSelected.value = gameAwardId;
  const gameAward = processGameAwards.value.find((award) => award.id.toString() === gameAwardId);
  if (
    !gameAward?.gameChoiceList.some(
      (choice) => choice.id.toString() === gameChoiceSelected.value.toString(),
    )
  ) {
    gameChoiceSelected.value = null;
  }
  emit("change", {
    id: gameChoiceSelected.value,
    gameAwardId: gameAwardSelected.value,
  });
};

const handleGameChoiceChange = function (gameChoiceId) {
  gameChoiceSelected.value = gameChoiceId;
  emit("change", {
    id: gameChoiceId,
    gameAwardId: gameAwardSelected.value,
  });
};

const deleteCondition = function () {
  emit("delete", condition.id);
};

// Watch
watch(
  () => condition.gameCondition,
  (value) => {
    gameChoiceSelected.value = value?.toString();
  },
);

onMounted(() => {
  if (condition.gameChoice) {
    const allGameChoices = gameAwards
      .map((ga) => (ga.gameChoiceList != null ? ga.gameChoiceList : []))
      .flat();
    const gameChoice = allGameChoices.filter((c) => c.id === condition.gameChoice);
    if (gameChoice && gameChoice[0]) {
      gameAwardSelected.value = gameChoice[0].gameAwardId.toString();
    }
  }
});
</script>
